/**
 * Bridge for the src-tauri online (mic) recording chunk-upload IPC commands.
 *
 * When the `release-apps-rust-chunk-upload-desktop` flag is on, Rust owns the online
 * chunk-upload loop (cadence timer, extraction, multipart upload, backup,
 * `:finish`). The frontend just hands over the auth token, endpoints, and
 * session/recording ids at start, pushes a refreshed token on a 401, and stops
 * the session, it never fetches audio bytes back over IPC.
 *
 * Mirrors `src/tauri/chronicle/session.ts` (same `IpcResponse` envelope
 * unwrap; no toast, callers own their error UX).
 */
import type { BackupMode, StopReason } from '@/tauri/controller/self-healing-stop';
import { isTauri } from '@/tauri/utils';

// ---------------------------------------------------------------------------
// Types (mirror the Rust structs in src-tauri/src/ipc/online_chunk_upload.rs)
// ---------------------------------------------------------------------------

export interface StartOnlineChunkUploadParams {
  sessionId: string;
  recordingId: string;
  /** ml-scribe base URL (`getScribeBaseUrlFromV2()`). */
  baseUrl: string;
  token: string;
  /**
   * Whether chunks upload to the EKS transcription service
   * (`/transcribe-chunk`) vs ml-scribe v1. Locked for the session in Rust.
   */
  useTranscriptionServiceV2: boolean;
  /**
   * EKS transcription base URL (`getTranscriptionBaseUrl()`, EKS internal /v1
   * path). Required when `useTranscriptionServiceV2` is true; `null` for v1.
   */
  transcriptionBaseUrl: string | null;
  /** BCP-47 language codes (v2 only; FE still owns the register PUT). */
  languages: string[];
  /** Cadence between chunk extractions (seconds); PostHog `desktop-chunk-size` (default 45). */
  chunkIntervalSecs: number;
  /**
   * When true, Rust force-refreshes Auth0 on chunk-upload 401. Locked at session start.
   */
  rustAuth0TokenRefresh: boolean;
}

/**
 * What `online_chunk_upload_stop` returns: the Rust `TerminalOutcome`. Every
 * backup field the stop flow reads must be on that wire, which the Rust test
 * `outcome_carries_the_raw_backup_disposition_the_fe_gate_reads` pins.
 */
export interface StopOnlineChunkUploadResult {
  totalChunks: number;
  totalBytes: number;
  failedChunks: number;
  durationMs: number;
  /**
   * Whether the authoritative full-recording backup (built from the lossless
   * source WAVs) was uploaded. The stop flow keeps the local WAVs + prompts a
   * backup upload when this is `false`, instead of advancing to note generation.
   */
  backupUploaded: boolean;
  /**
   * Whether `:finish` succeeded (always `true` for v2 EKS, which auto-finalises;
   * for v1 a failed `:finish` leaves the transcription session unfinalised). The
   * stop flow gates note generation on this together with `backupUploaded`, a
   * failed finalize must not advance to note gen on a possibly-incomplete
   * transcript.
   */
  finishSucceeded: boolean;
  /**
   * The backup MP3 was retained (encrypted, with a durable `backup_recovery`
   * row) and its upload detached from the stop: safe, not yet confirmed.
   * Only this field tells "still uploading" from "extraction failed".
   */
  backupPendingAsync: boolean;
  /**
   * The blocking backup upload failed or exceeded the stop budget, so the MP3
   * was retained (encrypted + a durable row) for the boot reconcile. No data
   * loss, but nothing is uploading right now.
   */
  backupRetained: boolean;
  /**
   * Whether this stop moved its source WAVs off the live paths (device-change
   * rotation or a detached-backup snapshot). Read it with `backupUploaded`:
   * `false` alone cannot tell a failed extraction from one not yet run.
   */
  backupDeferred: boolean;

  /**
   * Why this stop happened, as recorded on the terminal outcome. Optional
   * because FE-synthesised results (e.g. `terminalOutcomeAsStopResult`) have
   * none. A stop on an already-terminal epoch replays the original outcome, so
   * this reason, not `Ok`, tells a stop we ran from a replay.
   */
  reason?: StopReason;

  /**
   * The backup disposition Rust decided (`select_backup_mode`). Unlike the raw
   * flags above, `pendingAsync` here is decided only after `:finish` succeeded.
   */
  backup?: BackupMode;

  /**
   * The epoch this stop resolved to. Diagnostic only, not a replay
   * discriminator. Logging it shows which epoch a stop reached. Optional,
   * since an FE-synthesised result names no epoch.
   */
  recordingId?: string;
  generation?: number;
  /**
   * The local offline consult this stop handed its unsent audio to. Present
   * only when the stop sent `offlineHandover` and the network was down: the
   * reconnect sync uploads that audio into this session and generates the
   * note. It is what tells an offline-held session apart from one that needs an
   * upload now, since `failedChunks` still counts the chunks that failed.
   */
  offlineHandoverLocalSessionId?: string;
}

/**
 * The session settings a stop sends so Rust can hand audio it could not upload
 * to the offline queue (`src-tauri/src/offline_recording/handover.rs`). Sent
 * only with the offline-recording flag on. `dictationType` matters most: the
 * sync writes the transcribe mode back onto the server session, so omitting it
 * would switch a Dictate session to Transcribe.
 */
export interface OfflineHandoverArgs {
  templateId?: string;
  dictationType?: string;
  language?: string;
  outputLanguage?: string;
  noteStyle?: string;
}

/** Whether Rust still owns an online-chunk session after WebView reload. */
export interface ActiveOnlineChunkUpload {
  active: boolean;
  sessionId: string | null;
}

// ---------------------------------------------------------------------------
// Internal invoke helper
// ---------------------------------------------------------------------------

class OnlineChunkUploadBridgeError extends Error {
  /** Server-supplied error code, present when the Rust IPC handler errored. */
  readonly errorCode?: string;

  constructor(message: string, errorCode?: string) {
    super(message);
    this.name = 'OnlineChunkUploadBridgeError';
    this.errorCode = errorCode;
  }
}

/** Shape Rust returns via `Ok(IpcResponse::from(...))`. See `ipc/response.rs`. */
type IpcResponseEnvelope<D> = {
  result?: { data: D } | null;
  error?: { message: string; error_code?: string } | null;
};

async function invokeCommand<T>(
  command: string,
  payload?: Record<string, unknown>
): Promise<T> {
  if (!isTauri()) {
    throw new OnlineChunkUploadBridgeError(
      'Online chunk upload bridge is only available inside the Tauri runtime'
    );
  }

  const { invoke } = await import('@tauri-apps/api/core');
  const response = await invoke<IpcResponseEnvelope<T>>(command, payload ?? {});

  if (response?.error != null) {
    throw new OnlineChunkUploadBridgeError(
      response.error.message,
      response.error.error_code
    );
  }
  if (response?.result == null) {
    throw new OnlineChunkUploadBridgeError(
      `online chunk upload bridge: ${command} returned malformed IpcResponse`
    );
  }
  return response.result.data;
}

// ---------------------------------------------------------------------------
// Public bridge
// ---------------------------------------------------------------------------

export const onlineChunkUploadBridge = {
  /** Configure + start the Rust upload pipeline (recorder already started). */
  async startSession(params: StartOnlineChunkUploadParams): Promise<void> {
    return invokeCommand<void>('online_chunk_upload_start', { params });
  },

  /** Push a fresh token after an `online_chunk:token_refresh_needed` event. */
  async refreshToken(token: string): Promise<void> {
    return invokeCommand<void>('online_chunk_upload_refresh_token', {
      params: { token },
    });
  },

  /** Whether Rust still has an online-chunk session (for boot re-attach). */
  async getActive(): Promise<ActiveOnlineChunkUpload> {
    return invokeCommand<ActiveOnlineChunkUpload>('online_chunk_upload_get_active');
  },

  /**
   * Stop the session. By default (final stop) blocks until the recorder is stopped
   * and the final chunk + backup are uploaded, so note generation is safe afterwards.
   * With `{ backgroundBackup: true }` (device-change recovery) the backup extraction
   * stays synchronous but the upload tail runs detached so the new mic restarts fast.
   */
  async stopSession(opts?: {
    backgroundBackup?: boolean;
    /** Never read the source WAV after a terminal writer failure. */
    quarantineWav?: boolean;
    /**
     * The self-healing-stop epoch this stop targets. When omitted, Rust uses
     * `RecordingLifecycle`'s current epoch.
     */
    recordingId?: string;
    generation?: number;
    /**
     * Why this stop happened. Omit for the final user stop (Rust defaults to
     * `userStop`). `pause` and `deviceChange` are non-generating segment stops.
     * It is passed here because the reason is recorded in the terminal outcome.
     */
    reason?: StopReason;
    /**
     * Request the backup FFmpeg mux run detached instead of inline. Set from the
     * `release-desktop-detached-backup-mux` kill switch (default OFF); V2
     * transcription recordings also detach on a note-generating stop, never on a
     * pause. Rust may decline (`admit_detached_mux`); the result's
     * `backupPendingAsync` / `backupUploaded` say which happened.
     */
    detachedBackupMux?: boolean;
    /**
     * Session settings for handing unsent audio to the offline queue. Omit it
     * and the stop never hands over, exactly as before the handover existed.
     */
    offlineHandover?: OfflineHandoverArgs;
  }): Promise<StopOnlineChunkUploadResult> {
    return invokeCommand<StopOnlineChunkUploadResult>('online_chunk_upload_stop', {
      params: {
        backgroundBackup: opts?.backgroundBackup ?? false,
        detachedBackupMux: opts?.detachedBackupMux ?? false,
        quarantineWav: opts?.quarantineWav ?? false,
        recordingId: opts?.recordingId,
        generation: opts?.generation,
        reason: opts?.reason,
        offlineHandover: opts?.offlineHandover,
      },
    });
  },
};

export { OnlineChunkUploadBridgeError };
